import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { doctorService } from '../../services/doctor';
import { 
  MedicalExaminationData, 
  MedicalActionData, 
  PrescriptionData, 
  SupportRequestData, 
  ReferralData 
} from '../../services/doctor';

interface Patient {
  id: number;
  name: string;
  medical_record_number: string;
  age: number;
  gender: string;
  date_of_birth: string;
  phone: string;
  address: string;
  allergies?: string;
  insurance_type?: string;
}

interface Visit {
  id: number;
  patient_id: number;
  patient: Patient;
  visit_date: string;
  status: string;
  service_type: string;
  payment_method: string;
}

interface MedicalExamination {
  id: number;
  patient_id: number;
  visit_id: number;
  doctor_id: number;
  chief_complaint: string;
  history_of_present_illness?: string;
  physical_examination: any;
  diagnoses: Array<{
    code: string;
    name: string;
    category: string;
  }>;
  additional_notes?: string;
  examination_datetime: string;
  status: string;
}

interface MedicalAction {
  id: number;
  examination_id: number;
  action_code: string;
  action_name: string;
  action_category: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  final_price: number;
  billable: boolean;
  status: string;
}

interface Prescription {
  id: number;
  prescription_number: string;
  examination_id: number;
  patient_id: number;
  doctor_id: number;
  diagnosis?: string;
  notes?: string;
  total_items: number;
  total_amount: number;
  status: string;
  prescribed_at: string;
  items: PrescriptionItem[];
}

interface PrescriptionItem {
  id: number;
  prescription_id: number;
  medication_id: string;
  medication_name: string;
  medication_category: string;
  strength: string;
  unit: string;
  dosage: string;
  frequency: string;
  duration: number;
  duration_unit: string;
  instructions?: string;
  quantity_prescribed: number;
  quantity_dispensed: number;
  unit_price: number;
  total_price: number;
  status: string;
}

interface SupportRequest {
  id: number;
  request_number: string;
  examination_id: number;
  patient_id: number;
  request_type: string;
  test_name: string;
  indication: string;
  clinical_information?: string;
  urgent: boolean;
  priority: string;
  status: string;
  requested_at: string;
}

interface Referral {
  id: number;
  referral_number: string;
  examination_id: number;
  patient_id: number;
  referral_type: string;
  destination_name: string;
  specialist_type?: string;
  indication: string;
  clinical_summary: string;
  diagnosis: string;
  urgency_level: string;
  status: string;
  referral_date?: string;
  digital_signature?: string;
}

interface DashboardStats {
  total_patients_today: number;
  completed_examinations: number;
  pending_prescriptions: number;
  pending_support_requests: number;
  urgent_referrals: number;
  revenue_today: number;
}

interface DoctorState {
  // Patients and Visits
  patients: Patient[];
  currentPatient: Patient | null;
  visits: Visit[];
  currentVisit: Visit | null;
  
  // Examinations
  examinations: MedicalExamination[];
  currentExamination: MedicalExamination | null;
  
  // Medical Actions
  medicalActions: MedicalAction[];
  medicalActionsCatalog: any[];
  
  // Prescriptions
  prescriptions: Prescription[];
  currentPrescription: Prescription | null;
  medicationsCatalog: any[];
  
  // Support Requests
  supportRequests: SupportRequest[];
  
  // Referrals
  referrals: Referral[];
  currentReferral: Referral | null;
  
  // Dashboard
  dashboardStats: DashboardStats | null;
  recentActivities: any[];
  
  // UI State
  loading: {
    patients: boolean;
    examinations: boolean;
    prescriptions: boolean;
    supportRequests: boolean;
    referrals: boolean;
    dashboard: boolean;
  };
  
  error: {
    patients: string | null;
    examinations: string | null;
    prescriptions: string | null;
    supportRequests: string | null;
    referrals: string | null;
    dashboard: string | null;
  };
  
  // Filters and Search
  filters: {
    patientSearch: string;
    visitStatus: string;
    examinationDate: string;
    prescriptionStatus: string;
    referralStatus: string;
  };
}

const initialState: DoctorState = {
  patients: [],
  currentPatient: null,
  visits: [],
  currentVisit: null,
  examinations: [],
  currentExamination: null,
  medicalActions: [],
  medicalActionsCatalog: [],
  prescriptions: [],
  currentPrescription: null,
  medicationsCatalog: [],
  supportRequests: [],
  referrals: [],
  currentReferral: null,
  dashboardStats: null,
  recentActivities: [],
  loading: {
    patients: false,
    examinations: false,
    prescriptions: false,
    supportRequests: false,
    referrals: false,
    dashboard: false,
  },
  error: {
    patients: null,
    examinations: null,
    prescriptions: null,
    supportRequests: null,
    referrals: null,
    dashboard: null,
  },
  filters: {
    patientSearch: '',
    visitStatus: '',
    examinationDate: '',
    prescriptionStatus: '',
    referralStatus: '',
  },
};

// Async Thunks
export const fetchPatients = createAsyncThunk(
  'doctor/fetchPatients',
  async (params?: any) => {
    const response = await doctorService.getPatients(params);
    return response.data;
  }
);

export const fetchPatientDetails = createAsyncThunk(
  'doctor/fetchPatientDetails',
  async ({ patientId, visitId }: { patientId: number; visitId?: number }) => {
    const response = await doctorService.getPatientDetails(patientId, visitId);
    return response.data;
  }
);

export const createExamination = createAsyncThunk(
  'doctor/createExamination',
  async (data: MedicalExaminationData) => {
    const response = await doctorService.storeExamination(data);
    return response.data;
  }
);

export const updateExamination = createAsyncThunk(
  'doctor/updateExamination',
  async ({ id, data }: { id: number; data: Partial<MedicalExaminationData> }) => {
    const response = await doctorService.updateExamination(id, data);
    return response.data;
  }
);

export const fetchMedicalActionsCatalog = createAsyncThunk(
  'doctor/fetchMedicalActionsCatalog',
  async () => {
    const response = await doctorService.getMedicalActionsCatalog();
    return response.data;
  }
);

export const createMedicalActions = createAsyncThunk(
  'doctor/createMedicalActions',
  async (data: MedicalActionData) => {
    const response = await doctorService.storeMedicalActions(data);
    return response.data;
  }
);

export const fetchMedicationsCatalog = createAsyncThunk(
  'doctor/fetchMedicationsCatalog',
  async (search?: string) => {
    const response = await doctorService.getMedicationsCatalog(search);
    return response.data;
  }
);

export const createPrescription = createAsyncThunk(
  'doctor/createPrescription',
  async (data: PrescriptionData) => {
    const response = await doctorService.createPrescription(data);
    return response.data;
  }
);

export const createSupportRequest = createAsyncThunk(
  'doctor/createSupportRequest',
  async (data: SupportRequestData) => {
    const response = await doctorService.createSupportRequest(data);
    return response.data;
  }
);

export const fetchSupportRequests = createAsyncThunk(
  'doctor/fetchSupportRequests',
  async (params?: any) => {
    const response = await doctorService.getSupportRequests(params);
    return response.data;
  }
);

export const createReferral = createAsyncThunk(
  'doctor/createReferral',
  async (data: ReferralData) => {
    const response = await doctorService.createReferral(data);
    return response.data;
  }
);

export const applyDigitalSignature = createAsyncThunk(
  'doctor/applyDigitalSignature',
  async ({ referralId, signatureData }: { referralId: number; signatureData: string }) => {
    const response = await doctorService.applyDigitalSignature(referralId, signatureData);
    return response.data;
  }
);

export const completeVisit = createAsyncThunk(
  'doctor/completeVisit',
  async ({ visitId, notes }: { visitId: number; notes?: string }) => {
    const response = await doctorService.completeVisit(visitId, notes);
    return response.data;
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'doctor/fetchDashboardStats',
  async (dateRange?: { startDate: string; endDate: string }) => {
    const response = await doctorService.getDashboardStats(dateRange);
    return response.data;
  }
);

// Slice
const doctorSlice = createSlice({
  name: 'doctor',
  initialState,
  reducers: {
    // Filter actions
    setPatientSearch: (state, action: PayloadAction<string>) => {
      state.filters.patientSearch = action.payload;
    },
    setVisitStatusFilter: (state, action: PayloadAction<string>) => {
      state.filters.visitStatus = action.payload;
    },
    setExaminationDateFilter: (state, action: PayloadAction<string>) => {
      state.filters.examinationDate = action.payload;
    },
    setPrescriptionStatusFilter: (state, action: PayloadAction<string>) => {
      state.filters.prescriptionStatus = action.payload;
    },
    setReferralStatusFilter: (state, action: PayloadAction<string>) => {
      state.filters.referralStatus = action.payload;
    },
    
    // Current item setters
    setCurrentPatient: (state, action: PayloadAction<Patient | null>) => {
      state.currentPatient = action.payload;
    },
    setCurrentVisit: (state, action: PayloadAction<Visit | null>) => {
      state.currentVisit = action.payload;
    },
    setCurrentExamination: (state, action: PayloadAction<MedicalExamination | null>) => {
      state.currentExamination = action.payload;
    },
    setCurrentPrescription: (state, action: PayloadAction<Prescription | null>) => {
      state.currentPrescription = action.payload;
    },
    setCurrentReferral: (state, action: PayloadAction<Referral | null>) => {
      state.currentReferral = action.payload;
    },
    
    // Clear states
    clearError: (state, action: PayloadAction<keyof typeof state.error>) => {
      state.error[action.payload] = null;
    },
    resetDoctorState: (_state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Fetch Patients
    builder
      .addCase(fetchPatients.pending, (state) => {
        state.loading.patients = true;
        state.error.patients = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading.patients = false;
        state.visits = action.payload;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading.patients = false;
        state.error.patients = action.error.message || 'Failed to fetch patients';
      });

    // Fetch Patient Details
    builder
      .addCase(fetchPatientDetails.fulfilled, (state, action) => {
        state.currentPatient = action.payload.patient;
        state.currentVisit = action.payload.current_visit;
      });

    // Create Examination
    builder
      .addCase(createExamination.pending, (state) => {
        state.loading.examinations = true;
        state.error.examinations = null;
      })
      .addCase(createExamination.fulfilled, (state, action) => {
        state.loading.examinations = false;
        state.examinations.push(action.payload);
        state.currentExamination = action.payload;
      })
      .addCase(createExamination.rejected, (state, action) => {
        state.loading.examinations = false;
        state.error.examinations = action.error.message || 'Failed to create examination';
      });

    // Fetch Medical Actions Catalog
    builder
      .addCase(fetchMedicalActionsCatalog.fulfilled, (state, action) => {
        state.medicalActionsCatalog = action.payload;
      });

    // Create Medical Actions
    builder
      .addCase(createMedicalActions.fulfilled, (state, action) => {
        state.medicalActions.push(...action.payload);
      });

    // Fetch Medications Catalog
    builder
      .addCase(fetchMedicationsCatalog.fulfilled, (state, action) => {
        state.medicationsCatalog = action.payload;
      });

    // Create Prescription
    builder
      .addCase(createPrescription.pending, (state) => {
        state.loading.prescriptions = true;
        state.error.prescriptions = null;
      })
      .addCase(createPrescription.fulfilled, (state, action) => {
        state.loading.prescriptions = false;
        state.prescriptions.push(action.payload);
        state.currentPrescription = action.payload;
      })
      .addCase(createPrescription.rejected, (state, action) => {
        state.loading.prescriptions = false;
        state.error.prescriptions = action.error.message || 'Failed to create prescription';
      });

    // Create Support Request
    builder
      .addCase(createSupportRequest.pending, (state) => {
        state.loading.supportRequests = true;
        state.error.supportRequests = null;
      })
      .addCase(createSupportRequest.fulfilled, (state, action) => {
        state.loading.supportRequests = false;
        state.supportRequests.push(action.payload);
      })
      .addCase(createSupportRequest.rejected, (state, action) => {
        state.loading.supportRequests = false;
        state.error.supportRequests = action.error.message || 'Failed to create support request';
      });

    // Fetch Support Requests
    builder
      .addCase(fetchSupportRequests.fulfilled, (state, action) => {
        state.supportRequests = action.payload;
      });

    // Create Referral
    builder
      .addCase(createReferral.pending, (state) => {
        state.loading.referrals = true;
        state.error.referrals = null;
      })
      .addCase(createReferral.fulfilled, (state, action) => {
        state.loading.referrals = false;
        state.referrals.push(action.payload);
        state.currentReferral = action.payload;
      })
      .addCase(createReferral.rejected, (state, action) => {
        state.loading.referrals = false;
        state.error.referrals = action.error.message || 'Failed to create referral';
      });

    // Apply Digital Signature
    builder
      .addCase(applyDigitalSignature.fulfilled, (state, action) => {
        const index = state.referrals.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.referrals[index] = { ...state.referrals[index], ...action.payload };
        }
        if (state.currentReferral?.id === action.payload.id) {
          state.currentReferral = { ...state.currentReferral, ...action.payload };
        }
      });

    // Complete Visit
    builder
      .addCase(completeVisit.fulfilled, (state, action) => {
        const visitIndex = state.visits.findIndex(v => v.id === action.payload.visit.id);
        if (visitIndex !== -1) {
          state.visits[visitIndex] = { ...state.visits[visitIndex], ...action.payload.visit };
        }
        if (state.currentVisit?.id === action.payload.visit.id) {
          state.currentVisit = { ...state.currentVisit, ...action.payload.visit };
        }
      });

    // Fetch Dashboard Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading.dashboard = true;
        state.error.dashboard = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading.dashboard = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading.dashboard = false;
        state.error.dashboard = action.error.message || 'Failed to fetch dashboard stats';
      });
  },
});

export const {
  setPatientSearch,
  setVisitStatusFilter,
  setExaminationDateFilter,
  setPrescriptionStatusFilter,
  setReferralStatusFilter,
  setCurrentPatient,
  setCurrentVisit,
  setCurrentExamination,
  setCurrentPrescription,
  setCurrentReferral,
  clearError,
  resetDoctorState,
} = doctorSlice.actions;

export default doctorSlice.reducer;